import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useParams, useUserState } from '../../../../hooks';
import { getRecentContacts } from '../../../../modules/provider/Client';
import { IProviderClient } from '../../../../api/Providers/Clients/types';
import { PublicProviderProfile } from '../../../../modules/Api.types';
import ContactList from './ContactList';
import { matchWithSavedPhoneNumber } from '../../../../modules/provider/ClientFields';
import { ConfirmationDialog } from '../../../ui';

// Number of recent contacts to display
const RECENT_CONTACTS_COUNT = 8;

/**
 * Returns whether a contact's phone numbers contain the value in search.
 * @param contact The contact object
 * @param search The search string
 * @returns True if there is a match, otherwise false
 */
function contactPhoneNumberMatch(contact: IProviderClient, search: string): boolean {
  return !!contact.phones.find(phone => matchWithSavedPhoneNumber(phone.number, search));
}

/**
 * Returns whether a contact's name contains the value in search.
 * @param contact The contact object
 * @param search The search string
 * @returns True if there is a match, otherwise false
 */
function contactNameMatch(contact: IProviderClient, search: string): boolean {
  return contact.name.toLowerCase().indexOf(search.toLowerCase()) >= 0;
}

type ContactListContainerProps = {
  filterText?: string;
  contacts: IProviderClient[];
  provider: Pick<PublicProviderProfile, 'id' | 'creation_time' | 'profile_phase_one_complete_date'>;
  showInviteActions?: boolean,
  invitedClients?: { [key: number]: boolean; id?: boolean },
  onNewClientSaved?: (newClient: IProviderClient) => void;
  onContactSelect?: (contact: IProviderClient) => void;
  hideEmptyState?: boolean;
  // whether or not to highlight currently selected contact (default: true)
  highlightSelectedContact?: boolean;
  // whether or not to allow selection of this contact
  isContactDisabled?: (contact: IProviderClient) => boolean;
  // whether or not to hide this contact from showing
  isContactHidden?: (contact: IProviderClient) => boolean;
  borderRadius?: number;
  onClientInvited?: (id: number) => void;
};

const ContactListContainer = ({
  filterText,
  contacts,
  isContactDisabled,
  provider,
  showInviteActions,
  invitedClients = {},
  onNewClientSaved,
  isContactHidden,
  highlightSelectedContact,
  onContactSelect,
  hideEmptyState,
  borderRadius,
  onClientInvited,
}: ContactListContainerProps) => {
  const contactsOrEmpty = useMemo(() => (contacts?.length ? contacts : []), [contacts]);
  const { source, clientId } = useParams();
  const [
    viewedAddedList,
    setAddedListValue,
  ] = useUserState<number[] | null>('recentlyAddedClientViewedList');

  const [
    showIsDisabledContactInfoModal,
    setShowIsDisabledContactInfoModal,
  ] = useState<boolean>(false);
  const [selectedContactId, setSelectedContactId] = useState<number>(0);
  const isSearchingByPhoneNumber = useMemo(() => /^[\d\s\-()+.]+$/.test(filterText), [filterText]);

  const sortedAndFilteredContacts = useMemo<Array<IProviderClient>>(
    () => contactsOrEmpty.filter(contact => {
    // Exclude contacts that should be hidden based on directive configuration
      if (typeof isContactHidden === 'function' && isContactHidden(contact)) {
        return false;
      }
      // Check contact.name for a match.
      // Contact names can contain numbers, so we still do a name
      // search even if isSearchingByPhoneNum is true.
      if (filterText) {
        if (contactNameMatch(contact, filterText)) {
          return true;
        }
        // No name match, but if filterText is numeric, check for phone
        // number match.
        if (isSearchingByPhoneNumber && contactPhoneNumberMatch(contact, filterText)) {
          return true;
        }
        return false;
      }
      return true;
    }).sort((a, b) => a.name.localeCompare(b.name)), [
      filterText,
      contactsOrEmpty,
      isContactHidden,
      isSearchingByPhoneNumber,
    ],
  );

  const recentContacts = useMemo<Array<IProviderClient>>(
    () => getRecentContacts(contactsOrEmpty, RECENT_CONTACTS_COUNT)
      .filter(contact => !(typeof isContactHidden === 'function' && isContactHidden(contact))),
    [
      contactsOrEmpty,
      isContactHidden,
    ],
  );

  useEffect(() => {
    setSelectedContactId(Number(clientId) || 0);
  }, [clientId]);

  const handleContactSelect = (contact: IProviderClient) => {
    if (!isContactDisabled?.(contact)) {
      if (highlightSelectedContact !== false) {
        setSelectedContactId(contact.id);
      }

      onContactSelect(contact);
    }
  };

  const handleDisabledContactInfoClick = () => {
    setShowIsDisabledContactInfoModal(true);
  };

  const checkContactDisabled = (contact: IProviderClient): boolean => isContactDisabled?.(contact);

  return (
    <React.Fragment>
      <ContactList
        contacts={sortedAndFilteredContacts}
        recentContacts={recentContacts}
        onDisabledContactInfo={handleDisabledContactInfoClick}
        isContactDisabled={checkContactDisabled}
        isSearchingByPhoneNumber={isSearchingByPhoneNumber}
        provider={provider}
        selectedContactId={selectedContactId}
        showInviteActions={showInviteActions}
        source={source}
        invitedClients={invitedClients}
        onClientInvited={onClientInvited}
        searchText={filterText}
        onRecentContactsViewed={setAddedListValue}
        viewedAddedList={viewedAddedList}
        onContactSelect={handleContactSelect}
        onNewClientSaved={onNewClientSaved}
        showEmptyState={!hideEmptyState && contactsOrEmpty.length < 10}
        borderRadius={borderRadius}
      />
      {showIsDisabledContactInfoModal ? (
        <ConfirmationDialog
          show
          title="Both of these profiles were created by clients. Only clients can manage them."
          confirmText="Got It"
          onConfirm={() => setShowIsDisabledContactInfoModal(false)}
        />
      ) : null}
    </React.Fragment>
  );
};

export default ContactListContainer;
